import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Clock, Zap, ArrowRight, AlertTriangle, CheckCircle2, Timer, TrendingDown, Download } from "lucide-react";
import MiniTimer from "@/components/landing/MiniTimer";

const tasks = [
  {
    task: "Student Admission Eligibility Check",
    manual: {
      time: "5-8 min",
      pain: "DOB ରୁ Age calculate, Rule book ଖୋଲି Class check, mistake ହେଲେ ପୁଣି calculate",
    },
    app: {
      time: "10 sec",
      benefit: "DOB enter କଲେ — Age + Eligible Class ତୁରନ୍ତ ଦେଖାଇଦିଏ",
    },
    dailyCount: "10-15 students (Admission ସମୟରେ)",
    savedPerDay: "1-2 ଘଣ୍ଟା",
  },
  {
    task: "PM POSHAN / MDM Daily Calculation",
    manual: {
      time: "15-20 min",
      pain: "Student count ଅନୁସାରେ Rice, Dal, Oil, Vegetables ସବୁ ଅଲଗା ଅଲଗା calculate, rounding error ହୁଏ",
    },
    app: {
      time: "30 sec",
      benefit: "Student count ଦିଅ — ସମ୍ପୂର୍ଣ୍ଣ ingredient list ସହ quantity ତୁରନ୍ତ ready",
    },
    dailyCount: "ପ୍ରତିଦିନ 1 ଥର",
    savedPerDay: "15-20 ମିନିଟ୍",
  },
  {
    task: "MDM Monthly Report Generation",
    manual: {
      time: "2-3 ଘଣ୍ଟା",
      pain: "30 ଦିନର data ଯୋଡ଼ି, total calculate, format ରେ ଲେଖି report ତିଆରି — ବହୁତ ଥକା କାମ",
    },
    app: {
      time: "2 min",
      benefit: "Month select କଲେ — ସମ୍ପୂର୍ଣ୍ଣ Monthly Report auto-generate ହୋଇଯାଏ",
    },
    dailyCount: "ମାସକୁ 1 ଥର",
    savedPerDay: "2-3 ଘଣ୍ଟା/ମାସ",
  },
  {
    task: "Class V & VIII Board Marks Calculation",
    manual: {
      time: "3-5 min / student",
      pain: "ପ୍ରତି student ର subjects ର marks ଯୋଡ଼ି percentage calculate, 40+ students ପାଇଁ ଦିନ ଶେଷ",
    },
    app: {
      time: "15 sec / student",
      benefit: "Marks enter କଲେ — Total, Percentage, Pass/Fail ସବୁ ତୁରନ୍ତ ଦେଖାଏ",
    },
    dailyCount: "40+ students (Exam ସମୟରେ)",
    savedPerDay: "3-4 ଘଣ୍ଟା",
  },
  {
    task: "Leave Application / Official Letter Writing",
    manual: {
      time: "15-25 min",
      pain: "Format ମନେ ରଖି, ଅଳସୁଆ handwriting ରେ ଲେଖି, mistake ହେଲେ ପୁଣି fresh ଲେଖା",
    },
    app: {
      time: "1 min",
      benefit: "Type select କର, details ଭର — Clean formatted application ready to print/share",
    },
    dailyCount: "ସପ୍ତାହକୁ 2-3 ଥର",
    savedPerDay: "30-50 ମିନିଟ୍/ସପ୍ତାହ",
  },
  {
    task: "Salary Calculation",
    manual: {
      time: "20-30 min",
      pain: "Basic pay, DA, HRA, deductions ସବୁ manually calculate — ଗୋଟିଏ ଭୁଲ ହେଲେ ପୁରା ଗଣନା ନଷ୍ଟ",
    },
    app: {
      time: "30 sec",
      benefit: "Basic pay enter କଲେ — ସମ୍ପୂର୍ଣ୍ଣ salary breakdown ସହ net pay instantly",
    },
    dailyCount: "ମାସକୁ 1-2 ଥର",
    savedPerDay: "20-30 ମିନିଟ୍/ମାସ",
  },
];

export default function TimeSavingsSection({ downloadLink }) {
  return (
    <section data-testid="time-savings-section" className="py-12 md:py-20 bg-white">
      <div className="container mx-auto px-4 md:px-6 max-w-5xl">
        {/* Headline with urgency */}
        <div className="text-center mb-4">
          <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-red-50 text-red-600 text-xs font-semibold uppercase tracking-wide">
            <AlertTriangle className="w-3.5 h-3.5" />
            ଆପଣ ପ୍ରତିଦିନ ସମୟ ନଷ୍ଟ କରୁଛନ୍ତି
          </span>
        </div>
        <h2
          data-testid="time-savings-headline"
          className="text-3xl md:text-4xl font-bold text-slate-900 text-center leading-tight"
          style={{ fontFamily: "'Outfit', sans-serif" }}
        >
          ଦେଖନ୍ତୁ — Manual Calculation ରେ ଆପଣଙ୍କ <span className="text-red-600">କେତେ ସମୟ ନଷ୍ଟ</span> ହେଉଛି
        </h2>
        <p className="mt-3 text-center text-slate-600 text-sm md:text-base max-w-2xl mx-auto">
          ପ୍ରତିଟି calculation manually କଲେ ଯେତିକି ସମୟ ଲାଗେ, Teacher Utility Toolkit ରେ ସେଇ କାମ <strong className="text-green-700">ସେକେଣ୍ଡ ରେ</strong> ହୋଇଯାଏ। ନିଜେ ତୁଳନା କରନ୍ତୁ:
        </p>

        {/* Task comparison cards */}
        <div className="mt-10 space-y-5">
          {tasks.map((t, i) => (
            <Card
              key={i}
              data-testid={`time-task-card-${i}`}
              className="border border-slate-200 overflow-hidden shadow-sm hover:shadow-md transition-shadow"
            >
              <CardContent className="p-0">
                {/* Task header */}
                <div className="bg-slate-50 px-4 py-3 border-b border-slate-100">
                  <h3
                    className="font-bold text-slate-900 text-sm md:text-base"
                    style={{ fontFamily: "'Outfit', sans-serif" }}
                  >
                    {t.task}
                  </h3>
                  <p className="text-xs text-slate-500 mt-0.5">
                    <Timer className="w-3 h-3 inline mr-1" />
                    {t.dailyCount}
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2">
                  {/* Manual - Pain */}
                  <div className="p-4 bg-red-50/50 border-b md:border-b-0 md:border-r border-red-100">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-7 h-7 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                        <Clock className="w-3.5 h-3.5 text-red-600" />
                      </div>
                      <div>
                        <p className="text-[10px] font-semibold text-red-500 uppercase tracking-wider">Manually</p>
                        <p className="text-lg font-bold text-red-700" style={{ fontFamily: "'Outfit', sans-serif" }}>
                          {t.manual.time}
                        </p>
                      </div>
                    </div>
                    <p className="text-xs text-red-800/70 leading-relaxed">{t.manual.pain}</p>
                  </div>

                  {/* App - Solution */}
                  <div className="p-4 bg-green-50/50">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-7 h-7 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                        <Zap className="w-3.5 h-3.5 text-green-600" />
                      </div>
                      <div>
                        <p className="text-[10px] font-semibold text-green-500 uppercase tracking-wider">App ରେ</p>
                        <p className="text-lg font-bold text-green-700" style={{ fontFamily: "'Outfit', sans-serif" }}>
                          {t.app.time}
                        </p>
                      </div>
                    </div>
                    <p className="text-xs text-green-800/70 leading-relaxed">{t.app.benefit}</p>
                  </div>
                </div>

                {/* Time saved bar */}
                <div className="px-4 py-2 bg-green-700 flex items-center justify-between">
                  <span className="text-green-100 text-xs">ସମୟ ସଞ୍ଚୟ:</span>
                  <span className="text-white text-sm font-bold">{t.savedPerDay}</span>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Total savings summary */}
        <Card
          data-testid="total-savings-card"
          className="mt-8 border-2 border-green-600 bg-green-50 overflow-hidden shadow-lg"
        >
          <CardContent className="p-6 md:p-8 text-center">
            <TrendingDown className="w-10 h-10 text-green-700 mx-auto mb-3" />
            <h3
              className="text-2xl md:text-3xl font-bold text-slate-900"
              style={{ fontFamily: "'Outfit', sans-serif" }}
            >
              ପ୍ରତିଦିନ <span className="text-green-700">2-3 ଘଣ୍ଟା</span> ସମୟ ବଞ୍ଚାନ୍ତୁ
            </h3>
            <p className="mt-2 text-slate-600 text-sm md:text-base max-w-lg mx-auto">
              ମାସକୁ <strong className="text-green-700">60+ ଘଣ୍ଟା</strong> — ବର୍ଷକୁ <strong className="text-green-700">700+ ଘଣ୍ଟା</strong>। ଏହି ସମୟ ଆପଣ ପିଲାମାନଙ୍କୁ ପଢ଼ାଇବାରେ ଲଗାଇ ପାରିବେ!
            </p>

            <div className="mt-5 grid grid-cols-3 gap-3 max-w-sm mx-auto">
              <div className="bg-white rounded-xl p-3 border border-green-200">
                <p className="text-xl md:text-2xl font-bold text-green-700" style={{ fontFamily: "'Outfit', sans-serif" }}>2-3 hr</p>
                <p className="text-[10px] text-slate-500 uppercase font-medium mt-0.5">ପ୍ରତିଦିନ</p>
              </div>
              <div className="bg-white rounded-xl p-3 border border-green-200">
                <p className="text-xl md:text-2xl font-bold text-green-700" style={{ fontFamily: "'Outfit', sans-serif" }}>60+ hr</p>
                <p className="text-[10px] text-slate-500 uppercase font-medium mt-0.5">ପ୍ରତି ମାସ</p>
              </div>
              <div className="bg-white rounded-xl p-3 border border-green-200">
                <p className="text-xl md:text-2xl font-bold text-green-700" style={{ fontFamily: "'Outfit', sans-serif" }}>700+ hr</p>
                <p className="text-[10px] text-slate-500 uppercase font-medium mt-0.5">ପ୍ରତି ବର୍ଷ</p>
              </div>
            </div>

            <div className="mt-5 bg-amber-50 rounded-lg px-4 py-3 border border-amber-200 max-w-md mx-auto">
              <p className="text-sm text-amber-800 font-medium">
                <AlertTriangle className="w-4 h-4 inline mr-1 text-amber-600" />
                ଭାବନ୍ତୁ — ଆପଣ ପ୍ରତିବର୍ଷ <strong>700 ଘଣ୍ଟା</strong> Manual calculation ରେ ନଷ୍ଟ କରୁଛନ୍ତି। ଏହା <strong>29 ଦିନ</strong> ସମାନ — ପୁରା ଗୋଟିଏ ମାସ!
              </p>
            </div>

            <Button
              data-testid="time-savings-download-btn"
              asChild
              className="mt-6 h-12 px-8 bg-green-700 hover:bg-green-800 text-white rounded-full text-base font-semibold shadow-lg shadow-green-900/20"
            >
              <a href={downloadLink} target="_blank" rel="noopener noreferrer">
                <Download className="mr-2 w-4 h-4" />
                ଆଜି ହିଁ ସମୟ ବଞ୍ଚାଇବା ଆରମ୍ଭ କରନ୍ତୁ
              </a>
            </Button>
            <div className="mt-3">
              <a
                href="https://teacherutilitytoolkit.in/"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="timesave-try-online-btn"
                className="inline-flex items-center gap-2 h-11 px-6 bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white rounded-full text-sm font-bold shadow-lg shadow-orange-500/25 transition-all"
              >
                ⚡ ଏବେ ସିଧା Browser ରେ FREE ଚଲାନ୍ତୁ!
              </a>
            </div>
            <div className="mt-2 flex justify-center">
              <MiniTimer />
            </div>
            <p className="mt-2 text-xs text-green-600 font-medium">
              <CheckCircle2 className="w-3 h-3 inline mr-1" />
              ₹199/ବର୍ଷ — ₹4/ସପ୍ତାହ ରୁ କମ୍ ରେ 700+ ଘଣ୍ଟା ବଞ୍ଚାନ୍ତୁ
            </p>
          </CardContent>
        </Card>
      </div>
    </section>
  );
}
